// Candidate color + pattern resolution. Color is always ONE of three redundant
// signals (color + text label + shape/pattern token); it is never the sole cue.

import type { CandidateConfig, PatternToken } from '../types/config.ts';
import type { ThemeMode } from '../hooks/useTheme.ts';

/** The hue to use for a candidate under the active theme. */
export function candidateColor(cand: CandidateConfig, resolved: 'dark' | 'light'): string {
  if (resolved === 'dark' && cand.colorDark) return cand.colorDark;
  return cand.color;
}

/** A stable order helper: withdrawn candidates sink to the bottom. */
export function activeFirst(a: CandidateConfig, b: CandidateConfig): number {
  return Number(a.withdrawn ?? false) - Number(b.withdrawn ?? false);
}

export function resolvedTheme(mode: ThemeMode): 'dark' | 'light' {
  if (mode === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return mode;
}

/** SVG <path>/<rect> markup for a pattern tile, sized for a `size`×`size` box.
 *  Rendered inside a candidate token so the shape is a redundant, CVD-safe cue. */
export function patternTile(token: PatternToken, color: string, size = 12): string {
  const c = color;
  switch (token) {
    case 'solid':
      return `<rect width="${size}" height="${size}" fill="${c}"/>`;
    case 'stripe': // horizontal bars
      return `<rect width="${size}" height="${size}" fill="${c}" opacity="0.28"/>
              <rect y="1" width="${size}" height="2" fill="${c}"/>
              <rect y="6" width="${size}" height="2" fill="${c}"/>
              <rect y="11" width="${size}" height="1.5" fill="${c}"/>`;
    case 'diagonal':
      return `<rect width="${size}" height="${size}" fill="${c}" opacity="0.22"/>
              <path d="M-3 3 L3 -3 M0 ${size} L${size} 0 M${size - 3} ${size + 3} L${size + 3} ${size - 3}"
                    stroke="${c}" stroke-width="2"/>`;
    case 'grid':
      return `<rect width="${size}" height="${size}" fill="${c}" opacity="0.2"/>
              <path d="M0 6 H${size} M6 0 V${size}" stroke="${c}" stroke-width="1.5"/>`;
    case 'dot':
      return `<rect width="${size}" height="${size}" fill="${c}" opacity="0.2"/>
              <circle cx="4" cy="4" r="1.6" fill="${c}"/>
              <circle cx="9" cy="9" r="1.6" fill="${c}"/>`;
    case 'ring':
      return `<rect width="${size}" height="${size}" fill="${c}" opacity="0.18"/>
              <circle cx="6" cy="6" r="3.4" fill="none" stroke="${c}" stroke-width="1.8"/>`;
    default:
      return `<rect width="${size}" height="${size}" fill="${c}"/>`;
  }
}
